import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Search, X, Library } from 'lucide-react';
import { BaseType } from '../types/Enums';
import { LibraryTemplate } from '../types/LibraryTemplate';
import { LibraryApi } from '../api/libraryApi';
import { LibraryConfig } from '../library/LibraryConfig';

interface LibraryPickerModalProps<F extends object> {
    config: LibraryConfig<F>;
    open: boolean;
    onClose: () => void;
    onSelect: (template: LibraryTemplate<F>) => void;
    /**
     * Templates for this base type are listed first as suggested. Omit to list
     * all as one group, or per group when the config has its own grouping.
     */
    currentBaseType?: BaseType;
}

/**
 * Modal for picking a template from a superadmin library.
 * Shows templates grouped by: suggested (matching baseType) and other, or by
 * the config's own grouping (e.g. kontroll category) when it has one.
 *
 * Uses react-bootstrap Modal so it stacks correctly on top of an editor
 * modal: Escape and backdrop clicks close only this picker.
 */
export function LibraryPickerModal<F extends object>({
    config,
    open,
    onClose,
    onSelect,
    currentBaseType,
}: LibraryPickerModalProps<F>) {
    const api = useMemo(() => new LibraryApi<F>(config), [config]);
    const [searchTerm, setSearchTerm] = useState('');
    const [templates, setTemplates] = useState<LibraryTemplate<F>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load templates when modal opens
    useEffect(() => {
        if (open) {
            setSearchTerm('');
            setLoading(true);
            setError(null);
            api
                .getAllTemplates()
                .then(setTemplates)
                .catch((err) => {
                    console.error('Failed to load library templates:', err);
                    setError(config.labels.loadError);
                })
                .finally(() => setLoading(false));
        }
    }, [open, api, config.labels.loadError]);

    // Filter by search term (case-insensitive)
    const filteredTemplates = useMemo(() => {
        if (!searchTerm.trim()) {
            return templates;
        }
        const term = searchTerm.toLowerCase();
        return templates.filter((template) => template.translations.sv.toLowerCase().includes(term));
    }, [templates, searchTerm]);

    // Split into suggested (matching baseType) and other templates
    const { suggested, other } = useMemo(() => {
        const suggested: LibraryTemplate<F>[] = [];
        const other: LibraryTemplate<F>[] = [];

        filteredTemplates.forEach((template) => {
            if (currentBaseType !== undefined && template.baseTypes.includes(currentBaseType)) {
                suggested.push(template);
            } else {
                other.push(template);
            }
        });

        return { suggested, other };
    }, [filteredTemplates, currentBaseType]);

    // Per-group sections, for a library with its own grouping and no base type to suggest from
    const groupSections = useMemo(() => {
        const grouping = config.grouping;
        if (!grouping || currentBaseType !== undefined) return null;
        return grouping.groups
            .map((group) => ({
                ...group,
                templates: filteredTemplates.filter((template) => grouping.groupsOf(template).includes(group.key)),
            }))
            .filter((section) => section.templates.length > 0);
    }, [config.grouping, currentBaseType, filteredTemplates]);

    const renderTemplateRow = (template: LibraryTemplate<F>) => (
        <button
            key={template.id}
            type="button"
            className="list-group-item list-group-item-action"
            onClick={() => onSelect(template)}
        >
            <div className="d-flex justify-content-between align-items-center">
                <span className="fw-medium">{template.translations.sv}</span>
                <span className="text-muted small">{config.formatSummary(template)}</span>
            </div>
        </button>
    );

    return (
        <Modal show={open} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <Library size={20} className="me-2" />
                    {config.labels.pickerTitle}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Search input */}
                <div className="input-group mb-3">
                    <span className="input-group-text">
                        <Search size={18} />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Sök mall..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                    {searchTerm && (
                        <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm('')}
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Laddar...</span>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {groupSections?.map((section) => (
                            <div key={section.key} className="mb-3">
                                <h6 className="text-muted mb-2">
                                    {section.label} ({section.templates.length})
                                </h6>
                                <div className="list-group">{section.templates.map(renderTemplateRow)}</div>
                            </div>
                        ))}

                        {!groupSections && suggested.length > 0 && (
                            <div className="mb-3">
                                <h6 className="text-muted mb-2">
                                    Föreslagna mallar ({suggested.length})
                                </h6>
                                <div className="list-group">
                                    {suggested.map(renderTemplateRow)}
                                </div>
                            </div>
                        )}

                        {!groupSections && other.length > 0 && (
                            <div className="mb-3">
                                <h6 className="text-muted mb-2">
                                    {suggested.length > 0
                                        ? `Övriga mallar (${other.length})`
                                        : `Alla mallar (${other.length})`}
                                </h6>
                                <div className="list-group">
                                    {other.map(renderTemplateRow)}
                                </div>
                            </div>
                        )}

                        {filteredTemplates.length === 0 && (
                            <div className="text-center text-muted py-4">
                                {searchTerm ? (
                                    <p>Inga mallar matchar "{searchTerm}"</p>
                                ) : (
                                    <p>Inga mallar i biblioteket</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Avbryt
                </button>
            </Modal.Footer>
        </Modal>
    );
}
